import {
  Bars2Icon,
  Cog6ToothIcon,
  LightBulbIcon,
  ListBulletIcon,
  PencilIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/outline';
import {
  DragEvent,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Button } from '../../../../common/Atoms/Button';
import { InputTextAddon } from '../../../../common/Atoms/InputTextAddon';
import { TextArea } from '../../../../common/Atoms/TextArea';
import { Toggle } from '../../../../common/Atoms/Toggle';
import { Paragraph } from '../../../../common/Atoms/Typography/Paragraph';
import { classNames } from '../../../../common/lib/classNames';
import { LoadingOverlay } from '../../Molecules/LoadingOverlay';
import { useGetSelectedOrganisation } from '../../../hooks/useGetSelectedOrganisation';
import {
  AgentPrompt,
  useCreateAgentPromptMutation,
  useDeleteAgentPromptMutation,
  useGetAgentPromptsQuery,
  useGetToolApprovalsQuery,
  useSetToolApprovalMutation,
  useUpdateAgentPromptMutation,
} from '../../../services/api/aiAgentApi/aiAgent';
import { friendlyToolLabel } from '../lib/agentDisplay';

interface PromptDraft {
  title: string;
  instruction: string;
  enabled: boolean;
}

export function AgentsRules() {
  const currentOrg = useGetSelectedOrganisation();
  const organisationId = currentOrg?.id;

  const [newPrompt, setNewPrompt] = useState<PromptDraft>({
    title: ``,
    instruction: ``,
    enabled: true,
  });
  const [draftById, setDraftById] = useState<Record<string, PromptDraft>>({});
  const [workingPromptId, setWorkingPromptId] = useState<string | null>(null);
  const [pendingToolName, setPendingToolName] = useState<string | null>(null);

  const [orderedPrompts, setOrderedPrompts] = useState<AgentPrompt[]>([]);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [isReordering, setIsReordering] = useState(false);
  const [editingPromptId, setEditingPromptId] = useState<string | null>(null);

  const {
    data,
    isLoading,
    error,
  } = useGetAgentPromptsQuery({
    organisationId: organisationId || ``,
  }, {
    skip: !organisationId,
  });

  const [createPrompt, { isLoading: isCreating }] = useCreateAgentPromptMutation();
  const [updatePrompt, { isLoading: isUpdating }] = useUpdateAgentPromptMutation();
  const [deletePrompt, { isLoading: isDeleting }] = useDeleteAgentPromptMutation();

  const {
    data: toolApprovalsData,
    isLoading: isToolApprovalsLoading,
  } = useGetToolApprovalsQuery({
    organisationId: organisationId || ``,
  }, {
    skip: !organisationId,
  });

  const [setToolApproval, { isLoading: isSavingToolApproval }] = useSetToolApprovalMutation();

  const handleToggleTool = async(toolName: string, autoApprove: boolean) => {
    if (!organisationId) {
      return;
    }

    setPendingToolName(toolName);
    try {
      await setToolApproval({
        organisationId,
        toolName,
        autoApprove,
      });
    }
    finally {
      setPendingToolName(null);
    }
  };

  const prompts = useMemo(() => {
    return [...(data?.prompts || [])].sort((a, b) => a.position - b.position);
  }, [data?.prompts]);

  useEffect(() => {
    if (draggingId) {
      return;
    }

    setOrderedPrompts(prompts);
  }, [prompts, draggingId]);

  useEffect(() => {
    const nextDrafts: Record<string, PromptDraft> = {};

    prompts.forEach(prompt => {
      nextDrafts[prompt.id] = {
        title: prompt.title,
        instruction: prompt.instruction,
        enabled: prompt.enabled,
      };
    });

    setDraftById(nextDrafts);
  }, [prompts]);

  const handleCreatePrompt = async() => {
    if (!organisationId || !newPrompt.title.trim() || !newPrompt.instruction.trim()) {
      return;
    }

    await createPrompt({
      organisationId,
      title: newPrompt.title.trim(),
      instruction: newPrompt.instruction.trim(),
      enabled: newPrompt.enabled,
      position: prompts.length,
    });

    setNewPrompt({
      title: ``,
      instruction: ``,
      enabled: true,
    });
  };

  const handleDraftChange = (promptId: string, patch: Partial<PromptDraft>) => {
    setDraftById(prev => ({
      ...prev,
      [promptId]: {
        ...prev[promptId],
        ...patch,
      },
    }));
  };

  const handleSavePrompt = async(prompt: AgentPrompt) => {
    if (!organisationId) {
      return;
    }

    const draft = draftById[prompt.id];

    if (!draft || !draft.title.trim() || !draft.instruction.trim()) {
      return;
    }

    setWorkingPromptId(prompt.id);
    try {
      await updatePrompt({
        organisationId,
        id: prompt.id,
        title: draft.title.trim(),
        instruction: draft.instruction.trim(),
        enabled: draft.enabled,
      });

      setEditingPromptId(null);
    }
    finally {
      setWorkingPromptId(null);
    }
  };

  const handleDeletePrompt = async(promptId: string) => {
    if (!organisationId) {
      return;
    }

    if (!window.confirm(`Delete this rule?`)) {
      return;
    }

    setWorkingPromptId(promptId);
    try {
      await deletePrompt({
        organisationId,
        id: promptId,
      });
      if (editingPromptId === promptId) {
        setEditingPromptId(null);
      }
    }
    finally {
      setWorkingPromptId(null);
    }
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>, overId: string) => {
    event.preventDefault();

    if (!draggingId || draggingId === overId) {
      return;
    }

    setOrderedPrompts(prev => {
      const fromIndex = prev.findIndex(prompt => prompt.id === draggingId);
      const toIndex = prev.findIndex(prompt => prompt.id === overId);

      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
        return prev;
      }

      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);

      next.splice(toIndex, 0, moved);

      return next;
    });
  };

  const handleDragEnd = async() => {
    const wasDragging = draggingId;
    setDraggingId(null);

    if (!organisationId || !wasDragging) {
      return;
    }

    const changed = orderedPrompts
      .map((prompt, index) => ({ prompt, index }))
      .filter(({ prompt, index }) => prompt.position !== index);

    if (!changed.length) {
      return;
    }

    setIsReordering(true);
    try {
      await Promise.all(changed.map(({ prompt, index }) =>
        updatePrompt({
          organisationId,
          id: prompt.id,
          position: index,
        })));
    }
    finally {
      setIsReordering(false);
    }
  };

  if (!organisationId) {
    return null;
  }

  return (
    <div className={ `space-y-4` }>
      <div className={ `space-y-1` }>
        <Paragraph>
          { `Rules` }
        </Paragraph>
        <Paragraph
          size={ `sm` }
          variant={ `secondary` }
        >
          { `Guidance the AI agent follows when handling your customers. Drag to reorder - rules higher in the list take priority.` }
        </Paragraph>
      </div>
      <div className={ `rounded-lg border border-gray-200 bg-white shadow-xs p-4 space-y-3` }>
        <div className={ `flex items-center gap-2` }>
          <PlusCircleIcon className={ `h-5 w-5 text-paidniceGreen-600` } />
          <Paragraph>
            { `Create rule` }
          </Paragraph>
        </div>
        <InputTextAddon
          label={ `Title` }
          value={ newPrompt.title }
          name={ `title` }
          onChange={ ({ value }) => setNewPrompt(prev => ({ ...prev, title: String(value || ``) })) }
        />
        <TextArea
          label={ `Instruction` }
          value={ newPrompt.instruction }
          name={ `instruction` }
          rows={ 5 }
          onChange={ ({ value }) => setNewPrompt(prev => ({ ...prev, instruction: String(value || ``) })) }
        />
        <Toggle
          label={ `Enabled` }
          checked={ newPrompt.enabled }
          onChange={ ({ value }) => setNewPrompt(prev => ({ ...prev, enabled: value })) }
        />
        <Button
          loading={ isCreating }
          disabled={ isCreating || !newPrompt.title.trim() || !newPrompt.instruction.trim() }
          onClick={ handleCreatePrompt }
        >
          { `Create rule` }
        </Button>
      </div>

      <div className={ `rounded-lg border border-gray-200 bg-white shadow-xs p-4 space-y-4` }>
        <div className={ `flex items-center justify-between` }>
          <div className={ `flex items-center gap-2` }>
            <ListBulletIcon className={ `h-5 w-5 text-indigo-500` } />
            <Paragraph>
              { `Rules` }
            </Paragraph>
          </div>
          {
            isReordering &&
            <Paragraph
              size={ `xs` }
              variant={ `secondary` }
            >
              { `Saving order...` }
            </Paragraph>
          }
        </div>
        {
          isLoading &&
          <Paragraph variant={ `secondary` }>
            { `Loading rules...` }
          </Paragraph>
        }
        {
          !!error &&
          <Paragraph variant={ `error` }>
            { `Failed to load rules.` }
          </Paragraph>
        }
        {
          !isLoading && !orderedPrompts.length &&
          <Paragraph variant={ `secondary` }>
            { `No rules configured yet.` }
          </Paragraph>
        }
        <div className={ `space-y-2` }>
          {
            orderedPrompts.map(prompt => {
              const draft = draftById[prompt.id] || {
                title: prompt.title,
                instruction: prompt.instruction,
                enabled: prompt.enabled,
              };
              const isWorkingThisPrompt = workingPromptId === prompt.id;
              const isBeingDragged = draggingId === prompt.id;
              const isEditing = editingPromptId === prompt.id;

              return (
                <div
                  key={ prompt.id }
                  draggable
                  onDragStart={ () => setDraggingId(prompt.id) }
                  onDragOver={ event => handleDragOver(event, prompt.id) }
                  onDrop={ event => event.preventDefault() }
                  onDragEnd={ handleDragEnd }
                  className={ classNames(
                    `rounded-lg border bg-white transition-shadow`,
                    isBeingDragged ? `border-paidniceGreen-300 shadow-md opacity-80` : `border-gray-200`,
                  ) }
                >
                  {
                    !isEditing &&
                    <div className={ `flex items-start gap-2 px-3 py-2` }>
                      <div className={ `mt-1 shrink-0 cursor-grab rounded-sm p-1 text-gray-400` }>
                        <Bars2Icon className={ `h-5 w-5` } />
                      </div>
                      <div className={ `min-w-0 flex-1` }>
                        <div className={ `flex items-center gap-2` }>
                          <Paragraph size={ `sm` }>
                            { draft.title || `Untitled rule` }
                          </Paragraph>
                          <span className={ `inline-flex items-center gap-1.5` }>
                            <span
                              className={ classNames(
                                `h-1.5 w-1.5 rounded-full`,
                                draft.enabled ? `bg-paidniceGreen-500` : `bg-gray-300`,
                              ) }
                            />
                            <Paragraph
                              size={ `xs` }
                              variant={ `secondary` }
                            >
                              { draft.enabled ? `Enabled` : `Disabled` }
                            </Paragraph>
                          </span>
                        </div>
                        <Paragraph
                          size={ `xs` }
                          variant={ `secondary` }
                          className={ `mt-1 line-clamp-2` }
                        >
                          { draft.instruction || `No instruction text` }
                        </Paragraph>
                      </div>
                      <div className={ `flex items-center gap-1` }>
                        <Button
                          color={ `none` }
                          icon={ <PencilIcon className={ `w-4 h-4` } /> }
                          onClick={ () => setEditingPromptId(prompt.id) }
                          tooltip={ `Edit rule` }
                        />
                        <Button
                          color={ `none` }
                          onClick={ () => handleDeletePrompt(prompt.id) }
                          disabled={ isUpdating || isDeleting || isReordering }
                        >
                          { `Delete` }
                        </Button>
                      </div>
                    </div>
                  }
                  {
                    isEditing &&
                    <div className={ `space-y-3 p-3` }>
                      <InputTextAddon
                        label={ `Title` }
                        value={ draft.title }
                        name={ `title` }
                        onChange={ ({ value }) => handleDraftChange(prompt.id, { title: String(value || ``) }) }
                      />
                      <TextArea
                        label={ `Instruction` }
                        value={ draft.instruction }
                        name={ `instruction` }
                        rows={ 5 }
                        onChange={ ({ value }) => handleDraftChange(prompt.id, { instruction: String(value || ``) }) }
                      />
                      <Toggle
                        label={ `Enabled` }
                        checked={ draft.enabled }
                        onChange={ ({ value }) => handleDraftChange(prompt.id, { enabled: value }) }
                      />
                      <div className={ `flex gap-2` }>
                        <Button
                          loading={ isUpdating && isWorkingThisPrompt }
                          disabled={ isWorkingThisPrompt || isUpdating || isDeleting || !draft.title.trim() || !draft.instruction.trim() }
                          onClick={ () => handleSavePrompt(prompt) }
                        >
                          { `Save` }
                        </Button>
                        <Button
                          color={ `gray` }
                          disabled={ isWorkingThisPrompt || isUpdating || isDeleting }
                          onClick={ () => setEditingPromptId(null) }
                        >
                          { `Done` }
                        </Button>
                        <Button
                          color={ `paidniceRed` }
                          loading={ isDeleting && isWorkingThisPrompt }
                          disabled={ isWorkingThisPrompt || isUpdating || isDeleting }
                          onClick={ () => handleDeletePrompt(prompt.id) }
                        >
                          { `Delete` }
                        </Button>
                      </div>
                    </div>
                  }
                </div>
              );
            })
          }
        </div>
        <div className={ `flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3` }>
          <LightBulbIcon className={ `mt-0.5 h-5 w-5 shrink-0 text-amber-500` } />
          <Paragraph
            size={ `xs` }
            className={ `text-amber-900` }
          >
            { `Rule order is priority order. The agent reads from top to bottom, so keep your most important instructions at the top.` }
          </Paragraph>
        </div>
      </div>

      <div className={ `rounded-lg border border-gray-200 bg-white shadow-xs p-4 space-y-3` }>
        <div>
          <div className={ `flex items-center gap-2` }>
            <Cog6ToothIcon className={ `h-5 w-5 text-slate-500` } />
            <Paragraph>
              { `Tool automation` }
            </Paragraph>
          </div>
          <Paragraph
            size={ `xs` }
            variant={ `secondary` }
            className={ `mt-1` }
          >
            { `Choose which actions the agent can take on its own. Anything switched off will wait for your approval.` }
          </Paragraph>
        </div>
        <LoadingOverlay
          loading={ isToolApprovalsLoading }
          className={ `min-h-[48px]` }
        >
          {
            !isToolApprovalsLoading && !(toolApprovalsData?.preferences || []).length &&
            <Paragraph
              size={ `xs` }
              variant={ `secondary` }
            >
              { `No actions to configure yet.` }
            </Paragraph>
          }
          <div className={ `space-y-3` }>
            {
              (toolApprovalsData?.preferences || []).map(preference => (
                <Toggle
                  key={ preference.id }
                  label={ friendlyToolLabel(preference.tool_name) }
                  checked={ preference.auto_approve }
                  loading={ isSavingToolApproval && pendingToolName === preference.tool_name }
                  disabled={ isSavingToolApproval }
                  onChange={ ({ value }) => handleToggleTool(preference.tool_name, value) }
                />
              ))
            }
          </div>
        </LoadingOverlay>
      </div>
    </div>
  );
}
